import React, { useRef, useState, useEffect, useCallback } from 'react';

import { useRouter } from 'next/router';
import styled from 'styled-components';

import { SESSION_KEY_IS_TAB_NAV } from '~/extensions/pageScrollRestorationExtension';
import CarouselArrowIcon from '~/icons/components/CarouselArrowIcon';
import { Router } from '~/techstyle-shared/next-routes';
import {
  desktop,
  mobile,
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';

import { NUM_SKELETON_CATEGORY_TABS } from '../constants';
import useTabsAndBreadcrumbs from '../hooks/useTabsAndBreadcrumbs';
import {
  haloRingBase,
  haloRingHover,
  haloRingPressed,
} from '../styles/haloRing';
import {
  getFiltersForTab,
  saveFiltersForTab,
  setTabGroup,
} from '../utils/tabFilterCache';

const SkeletonContainer = styled.ul`
  list-style: none;
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;

  ${mobile`
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    gap: 8px;
    padding: 8px 16px;
    margin-bottom: 16px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const Container = styled.div`
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.gridV2CategoryTabContainerBorder};
  width: 100%;

  ${mobile`
    padding: 0px 8px;
    margin-bottom: 12px;
    border-bottom: none;
  `}
`;

const TabsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  width: fit-content;

  ${mobile`
    width: 100%;
  `}
`;

const ScrollWrapper = styled.div<{
  $showRightGradient: boolean;
  $showLeftGradient: boolean;
  $hideEdgeFade: boolean;
}>`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;

  ${desktop`
    width: 66vw;
    max-width: 1278px;
  `}

  ${mobile`
    width: 100%;
  `}

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 150px;
    background: linear-gradient(
      to left,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    );
    pointer-events: none;
    opacity: ${({ $showLeftGradient, $hideEdgeFade }) =>
      $hideEdgeFade || !$showLeftGradient ? 0 : 1};
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 150px;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    );
    pointer-events: none;
    opacity: ${({ $showRightGradient, $hideEdgeFade }) =>
      $hideEdgeFade || !$showRightGradient ? 0 : 1};
    transition: opacity 0.3s ease;
    z-index: 1;
  }
`;

const TabsList = styled.ul<{ $isOverflowing: boolean }>`
  list-style: none;
  display: flex;
  gap: 16px;
  overflow-x: auto;
  width: 100%;
  justify-content: ${({ $isOverflowing }) =>
    $isOverflowing ? 'flex-start' : 'center'};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobile`
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    gap: 8px;
  `}
`;

const TabLink = styled.a<{ $isActive?: boolean }>`
  padding: 16px 12px;
  text-decoration: none;
  color: inherit;
  font-size: 16px;
  font-weight: 350;
  line-height: 24px;
  transition: background-color 0.2s ease-in-out;
  position: relative;
  white-space: nowrap;
  display: inline-block;
  border-radius: 4px 4px 0 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    border-radius: 64px;
  }

  /* Gate hover on real hover-capable devices so taps on touch devices don't
     leave tabs stuck in the hover state. */
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) =>
        theme.colors.gridV2CategoryTabLinkBgHover};
    }
  }

  &:active {
    background-color: ${({ theme }) =>
      theme.colors.gridV2CategoryTabLinkBgPressed};
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    `
    font-weight: 700;
    pointer-events: none;

    &::after {
      background-color: ${theme.colors.gridV2CategoryTabLinkActiveUnderline};
    }
  `}

  ${mobile`
    padding: 8px 8px;
    font-size: 14px;
    line-height: 16px;

    ${({ $isActive, theme }) =>
      $isActive &&
      `
      padding-bottom: 0px;
      margin-bottom: 8px;
      &::after {
        bottom: -3px;
        left: 8px;
        right: 8px;
        height: 1px;
        background-color: ${theme.colors.gridV2CategoryTabLinkActiveUnderline};
      }
    `}
  `}
`;

const ScrollButton = styled.button<{
  $disabled: boolean;
  $direction: 'left' | 'right';
}>`
  background: white;
  border: none;
  padding: 0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  position: relative;

  svg {
    transform: ${({ $direction }) =>
      $direction === 'left' ? 'rotate(180deg)' : 'none'};
  }

  ${haloRingBase};

  &::before {
    border-radius: inherit;
  }

  &:hover::before {
    ${haloRingHover};
  }

  &:active::before {
    ${haloRingPressed};
  }

  ${mobile`
    display: none;
  `}
`;

interface CategoryTabsProps {
  /** Suppresses the left/right edge fade overlays — e.g. when rendered inside
   * the sticky scroll header, where a solid-white gradient clashes with the
   * translucent glass background. */
  hideEdgeFade?: boolean;
}

const CategoryTabs = ({ hideEdgeFade = false }: CategoryTabsProps) => {
  const { formatMessage } = useIntl();
  const { isMobile } = useBreakpoint();
  const { tabs, activeTab, isLoading } = useTabsAndBreadcrumbs();
  const router = useRouter();
  const currentQuery = router.asPath.split('?')[1] || '';
  const listRef = useRef<HTMLUListElement>(null);
  const activeTabRef = useRef<HTMLLIElement>(null);
  const [scrollState, setScrollState] = useState({
    isOverflowing: false,
    isAtStart: true,
    isAtEnd: false,
  });

  // we cache filters for each tab within a category group (e.g. Tops) so that
  // when users switch between tabs, their selected filters for each tab are
  // preserved.
  // However, when the category group changes (e.g. from Bottoms to Tops),
  // we want to clear the cache.
  const tabGroupId = tabs?.[0]?.urlPath;
  useEffect(() => {
    if (tabGroupId) {
      setTabGroup(tabGroupId);
    }
  }, [tabGroupId]);

  // Auto-save the current tab's query params so they can be restored later
  const activeTabUrlPath = activeTab?.urlPath;
  useEffect(() => {
    if (activeTabUrlPath) {
      saveFiltersForTab(activeTabUrlPath, currentQuery);
    }
  }, [activeTabUrlPath, currentQuery]);

  const handleTabClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetUrlPath: string
  ) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      return;
    }
    e.preventDefault();
    const cached = getFiltersForTab(targetUrlPath);
    const targetUrl = cached ? `${targetUrlPath}?${cached}` : targetUrlPath;
    // Signal pageScrollRestorationExtension to restore the saved scroll
    // position for the destination tab URL (or fall back to top on first
    // visit) instead of always scrolling to top on forward nav.
    sessionStorage.setItem(SESSION_KEY_IS_TAB_NAV, 'true');
    Router.push(targetUrl);
  };

  const checkScrollIsAtEnd = () => {
    if (!listRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = listRef.current;
    const isScrollable = scrollWidth > clientWidth;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;
    const isAtStart = scrollLeft <= 5;

    setScrollState({
      isOverflowing: isScrollable,
      isAtStart,
      isAtEnd,
    });
  };

  const scrollTabs = (direction: 'left' | 'right') => {
    if (isMobile || !listRef.current) {
      return;
    }

    const scrollAmount = listRef.current.clientWidth * 0.9;
    const newScrollLeft =
      direction === 'left'
        ? listRef.current.scrollLeft - scrollAmount
        : listRef.current.scrollLeft + scrollAmount;

    listRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const centerActiveTab = useCallback(() => {
    if (activeTabRef.current && listRef.current) {
      const list = listRef.current;
      const tab = activeTabRef.current;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const listWidth = list.clientWidth;

      // Center the active tab within the scrollable list.
      const targetScroll = tabLeft - listWidth / 2 + tabWidth / 2;
      list.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  }, []);

  // Use ResizeObserver to detect when the tab list has its final dimensions,
  // then check overflow state and center the active tab.
  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    const list = listRef.current;
    const observer = new ResizeObserver(() => {
      checkScrollIsAtEnd();
      centerActiveTab();
    });

    observer.observe(list);
    window.addEventListener('resize', checkScrollIsAtEnd);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkScrollIsAtEnd);
    };
  }, [tabs, centerActiveTab]);

  // Re-center when the active tab changes.
  useEffect(() => {
    centerActiveTab();
  }, [activeTab, centerActiveTab]);

  if (isLoading) {
    return (
      <SkeletonContainer>
        {Array.from({ length: NUM_SKELETON_CATEGORY_TABS }).map((_, index) => (
          <li key={index}>
            <SkeletonLine width={71} height={16} />
          </li>
        ))}
      </SkeletonContainer>
    );
  }

  if (!tabs?.length) {
    return null;
  }

  return (
    <Container>
      <TabsRow>
        {!isMobile && scrollState.isOverflowing && (
          <ScrollButton
            $disabled={scrollState.isAtStart}
            $direction="left"
            onClick={() => scrollTabs('left')}
            aria-label={formatMessage({
              id: 'grid_static.aria_scroll_left',
              defaultMessage: 'Scroll left',
            })}
          >
            <CarouselArrowIcon width={10} height={16} />
          </ScrollButton>
        )}
        <ScrollWrapper
          $showRightGradient={scrollState.isOverflowing && !scrollState.isAtEnd}
          $showLeftGradient={
            scrollState.isOverflowing && !scrollState.isAtStart
          }
          $hideEdgeFade={hideEdgeFade}
        >
          <TabsList
            ref={listRef}
            onScroll={checkScrollIsAtEnd}
            $isOverflowing={scrollState.isOverflowing}
          >
            {tabs.map(({ label, labelOverride, urlPath }) => (
              <li
                key={urlPath}
                ref={activeTab?.urlPath === urlPath ? activeTabRef : null}
              >
                <TabLink
                  href={urlPath}
                  $isActive={activeTab?.urlPath === urlPath}
                  onClick={(e) => handleTabClick(e, urlPath)}
                >
                  {labelOverride || label}
                </TabLink>
              </li>
            ))}
          </TabsList>
        </ScrollWrapper>
        {!isMobile && scrollState.isOverflowing && (
          <ScrollButton
            $disabled={scrollState.isAtEnd}
            $direction="right"
            onClick={() => scrollTabs('right')}
            aria-label={formatMessage({
              id: 'grid_static.aria_scroll_right',
              defaultMessage: 'Scroll right',
            })}
          >
            <CarouselArrowIcon width={10} height={16} />
          </ScrollButton>
        )}
      </TabsRow>
    </Container>
  );
};

export default CategoryTabs;
